import axios from 'axios';
import { startRegistration, startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';

// Axios instance kept as default export for compatibility
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

export default api;

// Attache le JWT (authentification réelle, voir AuthContext.jsx) à chaque requête sortante.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('od_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const delay = (ms = 500) => new Promise(r => setTimeout(r, ms));
const ok = (data) => ({ data });

// ─── Auth ──────────────────────────────────────────────────────
export const authService = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  sendOtp: (phone) => api.post('/auth/send-otp', { phone }),
  verifyOtp: (phone, code) => api.post('/auth/verify-otp', { phone, code }),
  requestPasswordReset: (phone) => api.post('/auth/reset-password/send-otp', { phone }),
  confirmPasswordResetOtp: (phone, code) => api.post('/auth/reset-password/verify-otp', { phone, code }),
  resetPassword: (phone, newPassword) => api.post('/auth/reset-password', { phone, newPassword }),
  // Second facteur SMS à la connexion (voir SecuriteSection.jsx / server/auth.js /login/verify-2fa).
  verifyLoginTwoFactor: (phone, code) => api.post('/auth/login/verify-2fa', { phone, code }),
};

// ─── Authentification 2FA (activer/désactiver depuis Sécurité) ─────────
export const twoFactorService = {
  status: () => api.get('/auth/2fa/status'),
  requestEnable: () => api.post('/auth/2fa/enable/send'),
  confirmEnable: (code) => api.post('/auth/2fa/enable/verify', { code }),
  disable: () => api.post('/auth/2fa/disable'),
};

// ─── Rides ─────────────────────────────────────────────────────
export const rideService = {
  getMyRides: async () => api.get('/rides/mine'),
  // Classement des quartiers · CO₂ évité — calculé à partir des vraies courses terminées (voir server/rides.js).
  getNeighborhoodStats: async () => api.get('/rides/stats/neighborhoods'),
  rate: async (id, rating, comment) => api.post(`/rides/${id}/rate`, { rating, comment }),
  // Suivi public (lien partagé, voir PublicTrackRide.jsx) — jamais authentifié
  // (un proche qui reçoit le lien n'a pas de compte OpenMove), et le serveur
  // ne renvoie volontairement que l'essentiel (pas de solde, pas de numéro du chauffeur).
  trackPublic: async (id) => api.get(`/rides/track/${id}`),
};

// ─── Tarification dynamique ────────────────────────────────────────────
// server/pricing.js calcule un vrai surge à partir de l'offre (chauffeurs en
// ligne à proximité) et de la demande (courses demandées récemment dans la zone).
export const pricingService = {
  getSurge: async ({ lat, lng }) => api.get('/pricing/surge', { params: { lat, lng } }),
};

// ─── Driver ────────────────────────────────────────────────────
// Ne parle plus qu'au vrai backend (voir server/drivers.js) — avant, tout ce
// qui suit (stats, gains, courses, avis, activités) retombait sur des
// données simulées le temps que ces routes soient construites côté serveur.
// updateOnlineStatus/acceptRide/rejectRide n'appellent plus rien : le vrai
// mécanisme est Socket.io (driverOnline/acceptRide/rejectRide, voir
// server/index.js et DriverContext.jsx/DriverDashboard.jsx) — ces routes
// REST n'ont jamais existé côté serveur, appeler un 404 à chaque clic
// n'apportait rien.
export const driverService = {
  // Positions anonymisées des chauffeurs réellement en ligne à proximité
  // (server/drivers.js) — utilisé pour les points affichés sur la carte de
  // réservation, pas de nom/note/id, juste des coordonnées légèrement
  // décalées pour la confidentialité tant qu'aucune course n'est en cours.
  getNearby: async ({ lat, lng }) => api.get('/drivers/nearby', { params: { lat, lng } }),
  updateAutoAccept: async (autoAccept) => api.post('/drivers/auto-accept', { autoAccept }),
  getEarnings: async () => api.get('/drivers/earnings'),
  getMyRides: async () => api.get('/drivers/rides'),
  getReviews: async () => api.get('/drivers/reviews'),
  getDriverStats: async () => api.get('/drivers/stats'),
  getRecentActivities: async () => api.get('/drivers/activities'),
};

// ─── Flotte ────────────────────────────────────────────────────
// Remplace l'ancien FleetContext.jsx (useReducer + localStorage) — voir
// server/fleet.js. getState (admin, flotte complète) / getMine (chauffeur,
// son seul véhicule assigné) sont les deux points d'entrée en lecture ;
// le reste correspond 1:1 aux anciens types d'action du reducer.
export const fleetService = {
  getState: async () => api.get('/fleet/state'),
  getMine: async () => api.get('/fleet/mine'),
  addVehicle: async (vehicle) => api.post('/fleet/vehicles', vehicle),
  updateVehicle: async (id, updates) => api.patch(`/fleet/vehicles/${id}`, { updates }),
  deleteVehicle: async (id) => api.delete(`/fleet/vehicles/${id}`),
  updateMileage: async (km) => api.patch('/fleet/mine/mileage', { km }),
  addMaintenanceType: async (mtype) => api.post('/fleet/maintenance-types', { mtype }),
  updateMaintenanceType: async (id, updates) => api.patch(`/fleet/maintenance-types/${id}`, { updates }),
  deleteMaintenanceType: async (id) => api.delete(`/fleet/maintenance-types/${id}`),
  addMaintenanceRecord: async (record) => api.post('/fleet/maintenance-records', { record }),
  updateMaintenanceRecord: async (id, updates) => api.patch(`/fleet/maintenance-records/${id}`, { updates }),
  deleteMaintenanceRecord: async (id) => api.delete(`/fleet/maintenance-records/${id}`),
  updateDocumentType: async (id, updates) => api.patch(`/fleet/document-types/${id}`, { updates }),
  addDocument: async (document) => api.post('/fleet/documents', { document }),
  deleteDocument: async (id) => api.delete(`/fleet/documents/${id}`),
  reportIssue: async (report) => api.post('/fleet/mine/reports', report),
  resolveIssue: async (id) => api.patch(`/fleet/reports/${id}/resolve`),
  setGeofence: async (geofence) => api.put('/fleet/geofence', { geofence }),
};

// ─── Payment ───────────────────────────────────────────────────
// Pas de vraie intégration Wave côté serveur — le stub honnête
// (processed:false) vient directement de server/payments.js, jamais imité ici.
export const paymentService = {
  initWave: (data) => api.post('/payments/wave', data),
};

// ─── Admin ─────────────────────────────────────────────────────
export const adminService = {
  // Compteurs opérationnels en direct (chauffeurs/courses/connectés).
  getLiveOps: async () => api.get('/admin/live-ops'),
  // Vrais clients (Postgres), avec courses/dépenses calculées depuis les
  // vraies courses terminées — voir server/admin.js.
  getUsers: async () => api.get('/admin/users'),
  // Revue des documents chauffeur (permis/carte grise/assurance).
  getDriversForVerification: async () => api.get('/admin/drivers/verification'),
  verifyDriver: async (id, decision, note) => api.post(`/admin/drivers/${id}/verify`, { decision, note }),
  // Diffuse une actualité en direct (Socket.io) aux chauffeurs/clients
  // connectés — voir server/admin.js POST /announcements.
  sendAnnouncement: async (title, message, target) => api.post('/admin/announcements', { title, message, target }),
  // Stats d'accueil, courses, chauffeurs, analytics/revenus, abonnements,
  // codes cadeaux et zones — remplacent les constantes codées en dur de
  // AdminDashboard.jsx (TOP_STATS, RECENT_RIDES, TOP_DRIVERS, AnalyticsView,
  // RevenueView, PAGES.subscriptions/promotions/zones). Voir server/admin.js.
  getOverview: async () => api.get('/admin/overview'),
  getRides: async (limit) => api.get('/admin/rides', { params: limit ? { limit } : undefined }),
  getDrivers: async () => api.get('/admin/drivers'),
  getAnalytics: async () => api.get('/admin/analytics'),
  getRevenue: async () => api.get('/admin/revenue'),
  getSubscriptions: async () => api.get('/admin/subscriptions'),
  getPromotions: async () => api.get('/admin/promotions'),
  getZones: async () => api.get('/admin/zones'),
};

// ─── Subscription ──────────────────────────────────────────────
export const subscriptionService = {
  subscribe: async (planId, paymentMethod) => api.post('/subscriptions/subscribe', { planId, paymentMethod }),
};

// ─── User Profile ──────────────────────────────────────────────
export const userService = {
  getProfile: async () => api.get('/users/profile'),
  updateProfile: async (data) => api.put('/users/profile', data),
  changePassword: async (currentPassword, newPassword) => api.post('/users/change-password', { currentPassword, newPassword }),
  // Upload d'un document de vérification chauffeur (permis/carte grise/assurance).
  uploadDocument: async (type, fileName, fileData) => api.post('/drivers/documents', { type, fileName, fileData }),
  getEmergencyContacts: async () => api.get('/users/emergency-contacts'),
  addEmergencyContact: async (contact) => api.post('/users/emergency-contacts', contact),
  deleteEmergencyContact: async (id) => api.delete(`/users/emergency-contacts/${id}`),
  getFavorites: async () => api.get('/users/favorites'),
  addFavorite: async (favorite) => api.post('/users/favorites', favorite),
  deleteFavorite: async (id) => api.delete(`/users/favorites/${id}`),
  useFavorite: async (id) => api.post(`/users/favorites/${id}/use`),
  getCustomEvents: async () => api.get('/users/custom-events'),
  addCustomEvent: async (name, dateISO, zones) => api.post('/users/custom-events', { name, dateISO, zones }),
  deleteCustomEvent: async (key) => api.delete(`/users/custom-events/${key}`),
  getWallet: async () => api.get('/users/wallet'),
  addWalletTransaction: async (tx) => api.post('/users/wallet/transactions', tx),
  // "Diviser la course" — demandes réelles adressées à d'autres comptes OpenMove (voir server/users.js).
  getSplitRequests: async () => api.get('/users/split-requests'),
  respondSplitRequest: async (id, accept) => api.post(`/users/split-requests/${id}/respond`, { accept }),
  // Cagnotte OpenScore — incrémente réellement les points déjà dépensés (voir server/users.js).
  addRedeemedPoints: async (points) => api.post('/users/redeemed-points', { points }),
};

// ─── Biométrie (WebAuthn) ────────────────────────────────────────
// Vrai protocole WebAuthn (@simplewebauthn/browser) : startRegistration /
// startAuthentication déclenchent le vrai capteur de l'appareil (Face ID,
// empreinte, Windows Hello) via navigator.credentials — pas une simulation.
export const webauthnService = {
  isSupported: () => browserSupportsWebAuthn(),
  status: async () => api.get('/webauthn/status'),
  register: async (label) => {
    const { data: optionsJSON } = await api.get('/webauthn/register/options');
    const response = await startRegistration({ optionsJSON });
    return api.post('/webauthn/register/verify', { response, label });
  },
  // Bascule principale : retire TOUS les appareils enregistrés.
  removeCredential: async () => api.delete('/webauthn/credential'),
  // Gestion multi-appareils (SecuriteSection.jsx) : retirer/renommer un seul appareil sans toucher aux autres.
  removeOneCredential: async (credentialId) => api.delete(`/webauthn/credential/${credentialId}`),
  renameCredential: async (credentialId, label) => api.patch(`/webauthn/credential/${credentialId}`, { label }),
  // Utilisé par LoginPage.jsx : tente une connexion biométrique complète.
  // Avec un numéro, cible directement les identifiants de ce compte. Sans
  // numéro (phone omis), flux "sans identifiant" : le navigateur propose les
  // comptes biométriques déjà enregistrés sur cet appareil (voir
  // server/webauthn.js) — plus besoin de taper son numéro avant Face
  // ID/empreinte. Échoue proprement si aucun identifiant n'est enregistré,
  // ou si l'utilisateur annule l'invite du système.
  loginWithBiometrics: async (phone) => {
    const { data: optionsJSON } = await api.post('/webauthn/login/options', phone ? { phone } : {});
    const { sessionId, ...publicKeyOptions } = optionsJSON;
    const response = await startAuthentication({ optionsJSON: publicKeyOptions });
    return api.post('/webauthn/login/verify', phone ? { phone, response } : { sessionId, response });
  },
};

// ─── Notifications ─────────────────────────────────────────────
export const notificationService = {
  getAll: async () => api.get('/notifications'),
  // SMS réel (server/notifications.js) — le serveur répond honnêtement
  // `sent: false` tant qu'aucun compte SMS n'est configuré (jamais un faux succès).
  sendSMS: async (to, message) => api.post('/notifications/sms', { to, message }),
};

// Codes cadeaux (server/giftCodes.js) — un vrai code, un vrai transfert de valeur entre deux portefeuilles.
export const giftService = {
  create: async (amountFcfa) => api.post('/gifts', { amountFcfa }),
  redeem: async (code) => api.post(`/gifts/${encodeURIComponent(code)}/redeem`),
};

// ─── OpenMove Corporate (server/corporate.js) ───────────────────────────
// Formulaire public de demande de contact B2B (pas une course, pas de compte requis).
export const corporateService = {
  submit: async (payload) => api.post('/corporate', payload),
  // Admin uniquement (CorporateLeadsView.jsx).
  list: () => api.get('/corporate'),
  updateStatus: (id, status) => api.patch(`/corporate/${encodeURIComponent(id)}/status`, { status }),
};

// ─── GPS flotte (traceurs LKGPS2 via LKGPS ou Flespi) ───────────────────
// Une position de traceur réel n'a pas de sens à simuler — le repli "non
// configuré" (voir /gps/status) vient déjà du serveur tant que ni
// LKGPS_USER/LKGPS_PASSWORD ni FLESPI_TOKEN ne sont renseignés.
export const gpsService = {
  status: () => api.get('/gps/status'),
  positions: (deviceIds) => api.get('/gps/positions', { params: { ids: deviceIds.join(',') } }),
  history: (deviceId) => api.get('/gps/history', { params: { id: deviceId } }),
  remoteHistory: (deviceId, startMs, endMs) => api.get('/gps/remote-history', { params: { id: deviceId, start: startMs, end: endMs } }),
};
